/**
 * useNestedList — generic CRUD hook for resources nested under a person.
 *
 * Encapsulates the list query + create/update/delete mutations that every
 * wizard step like Employment / Travel / Children / Marriage / Criminal
 * needs. Optimistic cache updates are done via setQueryData. Saving any
 * record invalidates the case evaluation so readiness updates instantly.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

interface CrudClient<TList, TItem, TCreate> {
  list: (caseId: string, personId: string) => Promise<TList>;
  create: (caseId: string, personId: string, req: TCreate) => Promise<TItem>;
  update: (
    caseId: string,
    personId: string,
    itemId: string,
    req: TCreate,
  ) => Promise<TItem>;
  delete: (caseId: string, personId: string, itemId: string) => Promise<void>;
}

interface ListEnvelope<TItem> {
  items: TItem[];
  total: number;
}

export function useNestedList<
  TItem extends { id: string },
  TCreate,
  TList extends ListEnvelope<TItem> = ListEnvelope<TItem>,
>(
  /** Logical resource name — used as part of the query key for caching. */
  resource: string,
  client: CrudClient<TList, TItem, TCreate>,
  caseId: string | undefined,
  personId: string | undefined,
) {
  const qc = useQueryClient();
  const key = ["case", caseId, "person", personId, resource] as const;

  const query = useQuery({
    queryKey: key,
    queryFn: () => client.list(caseId!, personId!),
    enabled: !!caseId && !!personId,
  });

  const upsertCache = (next: TItem) => {
    qc.setQueryData<TList>(key, (prev) => {
      if (!prev) return { items: [next], total: 1 } as TList;
      const idx = prev.items.findIndex((x) => x.id === next.id);
      const items =
        idx === -1 ? [...prev.items, next] : prev.items.map((x, i) => (i === idx ? next : x));
      return {
        ...prev,
        items,
        total: idx === -1 ? prev.total + 1 : prev.total,
      } as TList;
    });
    qc.invalidateQueries({ queryKey: ["case", caseId, "evaluation"] });
    // Nested entities (addresses, employments, marriages, children, …)
    // feed into the rendered snapshot — flag packet-status as outdated
    // so the review page resurfaces the "Re-generate" button.
    qc.invalidateQueries({ queryKey: ["case", caseId, "packet-status"] });
  };

  const create = useMutation({
    mutationFn: (req: TCreate) => client.create(caseId!, personId!, req),
    onSuccess: upsertCache,
  });

  const update = useMutation({
    mutationFn: ({ id, req }: { id: string; req: TCreate }) =>
      client.update(caseId!, personId!, id, req),
    onSuccess: upsertCache,
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.delete(caseId!, personId!, id),
    onSuccess: (_, id) => {
      qc.setQueryData<TList>(key, (prev) =>
        prev
          ? ({
              ...prev,
              items: prev.items.filter((x) => x.id !== id),
              total: prev.total - 1,
            } as TList)
          : prev,
      );
      qc.invalidateQueries({ queryKey: ["case", caseId, "evaluation"] });
      qc.invalidateQueries({ queryKey: ["case", caseId, "packet-status"] });
    },
  });

  return {
    items: query.data?.items ?? [],
    isLoading: query.isLoading,
    error: query.error,
    create,
    update,
    remove,
  };
}
